import clsx from "clsx";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import {
  Address,
  Asset,
  Balance,
  BalanceAccount,
  BCH,
  BTC,
  DOGE,
  LTC,
  THOR,
  TxResult,
  networkLabel,
  signers,
  validateAddress,
} from "rujira.js";
import {
  Animated,
  clipAmountString,
  Decimal,
  Fiat,
  LoaderWithContent,
  NetworkIcon,
  Radio,
  SimulationState,
  TransactionFeeItem,
  TransactionFeeSummary,
  TransferTimeValue,
  Warning,
  useTranslation,
} from "rujira.ui";
import { isSameTransferAsset } from "../../services/transferAsset";
import { useAccounts } from "../../services/accounts";
import {
  TransactionAddress,
  TransactionDetailRow,
  TransactionDetails,
  TransferAddressWarning,
  TransferAccountOption,
  TransferAccountSelector,
  TransferAmountField,
  TransferForm,
  TransferModalHeader,
  TransferModalNavigation,
  compareBigIntDesc,
  providersForAddress,
  transferAccountKey,
  transferFeeValueUsd,
  transferSimulationFees,
} from "./TransferModal";
import { usePreloadedBalances } from "./Balance";

const UTXO_NETWORKS = new Set([BTC, BCH, DOGE, LTC]);

export type DepositDetailSubmitProps = {
  selected?: BalanceAccount;
  amount: bigint;
  target?: string;
  quote: signers.cosmos.QuoteSwap | Error | "loading" | undefined;
  shouldSwap: boolean;
  onSuccess: (tx: TxResult) => void;
  cancel?: () => void;
  disabled?: boolean;
  isDangerous?: boolean;
  onSimulation?: (state: SimulationState) => void;
};

export const DepositDetail: FC<{
  asset: Asset;
  balance?: Balance;
  targets: Address[];
  target?: Address;
  Submit: FC<DepositDetailSubmitProps>;
  onSuccess: (tx: TxResult) => void;
  cancel: () => void;
  onBack?: () => void;
  connect: () => void;
  queryClient: signers.cosmos.QueryClient & signers.cosmos.ThorchainExtension;
  amount?: bigint;
  inboundAddresses?: { dustThreshold: bigint; chain: string }[];
  progress?: ReactNode;
  description?: ReactNode;
  showDestinationSuffix?: boolean;
}> = ({
  asset,
  balance,
  targets,
  target: initialTarget,
  Submit,
  onSuccess,
  cancel,
  onBack,
  connect,
  queryClient,
  amount: initialAmount,
  inboundAddresses,
  progress,
  description,
  showDestinationSuffix = true,
}) => {
  const { t } = useTranslation("common");
  const { accounts: connectedAccounts } = useAccounts();
  const balances = usePreloadedBalances();
  const accounts = useMemo(() => {
    const items = [...(balance?.accounts || [])];
    const totals = items.reduce(
      (result, item) =>
        result.set(
          item.asset.chain,
          (result.get(item.asset.chain) || 0n) + item.valueUsd
        ),
      new Map<string, bigint>()
    );
    return items.sort(
      (a, b) =>
        compareBigIntDesc(
          totals.get(a.asset.chain) || 0n,
          totals.get(b.asset.chain) || 0n
        ) ||
        a.asset.chain.localeCompare(b.asset.chain) ||
        compareBigIntDesc(a.valueUsd, b.valueUsd)
    );
  }, [balance]);
  const [sourceId, setSourceId] = useState(
    accounts[0] ? transferAccountKey(accounts[0]) : ""
  );
  const account =
    accounts.find((candidate) => transferAccountKey(candidate) === sourceId) ||
    accounts[0];
  const destinationOptions = useMemo<TransferAccountOption[]>(
    () =>
      targets.map((item) => ({
        id: item.address,
        address: item.address,
        network: THOR,
        providers: providersForAddress(connectedAccounts || [], item.address),
      })),
    [connectedAccounts, targets]
  );
  const [destinationId, setDestinationId] = useState(
    initialTarget?.address || destinationOptions[0]?.id || ""
  );
  const [customRecipient, setCustomRecipient] = useState(
    destinationOptions.length === 0
  );
  const [customAddress, setCustomAddress] = useState("");
  const [amount, setAmount] = useState(initialAmount || 0n);
  const [quote, setQuote] = useState<
    signers.cosmos.QuoteSwap | Error | "loading"
  >();
  const [simulation, setSimulation] = useState<SimulationState>();
  const [advanced, setAdvanced] = useState(false);
  const [direct, setDirect] = useState(false);
  const [hoverAdvanced, setHoverAdvanced] = useState(false);

  useEffect(() => {
    const first = destinationOptions[0];
    if (!first || destinationId || !customRecipient || customAddress.trim())
      return;
    setDestinationId(first.id);
    setCustomRecipient(false);
  }, [customAddress, customRecipient, destinationId, destinationOptions]);

  useEffect(() => {
    if (
      customRecipient ||
      !destinationId ||
      destinationOptions.some((option) => option.id === destinationId)
    )
      return;
    const first = destinationOptions[0];
    if (first) {
      setDestinationId(first.id);
    } else {
      setDestinationId("");
      setCustomRecipient(true);
    }
    setSimulation(undefined);
  }, [customRecipient, destinationId, destinationOptions]);

  useEffect(() => {
    if (!account) return;
    const nextSourceId = transferAccountKey(account);
    if (sourceId === nextSourceId) return;
    setSourceId(nextSourceId);
    setAmount(0n);
    setSimulation(undefined);
  }, [account, sourceId]);

  const trimmedCustomAddress = customAddress.trim();
  const destination = customRecipient ? trimmedCustomAddress : destinationId;
  const isCustomInvalid =
    customRecipient &&
    Boolean(trimmedCustomAddress) &&
    !validateAddress(THOR, trimmedCustomAddress);
  const canSwap = Boolean(
    account && !isSameTransferAsset(asset, account.asset)
  );
  const advancedOptionsApplicable = canSwap && asset.chain !== THOR;
  const shouldSwap = canSwap && !direct;
  const canonicalAssetLabel = t("assetOnNetwork", {
    asset: asset.metadata.symbol,
    network: networkLabel(asset.chain),
  });
  const directAssetLabel = t("assetOnNetwork", {
    asset: account?.asset.metadata.symbol || asset.metadata.symbol,
    network: account ? networkLabel(account.asset.chain) : "",
  });
  const depositAsTooltip = t("depositAsTooltip", {
    canonicalAsset: canonicalAssetLabel,
    selectedAsset: directAssetLabel,
  });
  const dustThreshold = inboundAddresses?.find(
    (item) => item.chain === account?.asset.chain
  )?.dustThreshold;
  const amountBelowMinimum = Boolean(
    amount > 0n && dustThreshold && amount < dustThreshold
  );

  const amountExceedsBalance = Boolean(account && amount > account.balance);

  useEffect(() => {
    setDirect(false);
  }, [account?.asset.asset, asset.asset]);

  useEffect(() => {
    if (!advancedOptionsApplicable) setAdvanced(false);
  }, [advancedOptionsApplicable]);

  useEffect(() => {
    setQuote(undefined);
    if (
      !account ||
      !shouldSwap ||
      amount <= 0n ||
      !destination ||
      isCustomInvalid ||
      amountExceedsBalance ||
      amountBelowMinimum
    )
      return;
    let cancelled = false;
    setQuote("loading");
    const timer = setTimeout(() => {
      queryClient.thorchain
        .getSwapQuote({
          fromAsset: account.asset.asset,
          toAsset: asset.asset,
          amount,
          streamingInterval: 1n,
          streamingQuantity: 0n,
          destination,
        })
        .then((result) => {
          if (!cancelled) setQuote(result);
        })
        .catch((error) => {
          if (!cancelled) setQuote(error);
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      cancelled = true;
    };
  }, [
    account,
    amount,
    amountBelowMinimum,
    amountExceedsBalance,
    isCustomInvalid,
    asset,
    destination,
    queryClient,
    shouldSwap,
  ]);

  const quotedReceived =
    typeof quote === "object" && "memo" in quote
      ? quote.expectedAmountOut
      : amount;
  const fee =
    (typeof quote === "object" && "memo" in quote && quote.fees?.total) || 0n;
  const price = asset.price?.current || 0n;
  const receivedAsset = direct && account ? account.asset : asset;
  const receivedPrice = receivedAsset.price?.current || 0n;
  const networkFee =
    simulation?.status === "completed" ? simulation.simulation : undefined;
  const networkFees =
    simulation?.status === "completed"
      ? transferSimulationFees(simulation)
      : [];
  const sourceAmountAfterNetworkFee =
    networkFee &&
    UTXO_NETWORKS.has(account?.asset.chain ?? THOR) &&
    account &&
    account.balance <= amount + networkFee.amount
      ? account.balance > networkFee.amount
        ? account.balance - networkFee.amount
        : 0n
      : amount;
  const received =
    sourceAmountAfterNetworkFee < amount && amount > 0n
      ? (quotedReceived * sourceAmountAfterNetworkFee) / amount
      : quotedReceived;
  const swapFeeValueUsd = (price * fee) / 10n ** 12n;
  const networkFeeItems: TransactionFeeItem[] = networkFees
    .filter((item) => item.amount > 0n)
    .map((item, index) => {
      const feePrice = balances.find(
        (balance) => balance.asset.metadata.symbol === item.symbol
      )?.asset.price?.current;
      return {
        key: `network-${index}`,
        label: t("networkFee"),
        ...item,
        valueUsd: transferFeeValueUsd(item.amount, item.decimals, feePrice),
      };
    });
  const swapFeeItem: TransactionFeeItem | undefined =
    fee > 0n
      ? {
          key: "swap",
          label: t("swapFee"),
          symbol: asset.metadata.symbol,
          amount: fee,
          decimals: 8,
          valueUsd: swapFeeValueUsd,
        }
      : undefined;
  const fees = [...networkFeeItems, swapFeeItem].filter(
    (item): item is TransactionFeeItem => item !== undefined
  );
  const totalFeeValueUsd = fees.reduce(
    (total, item) => total + (item.valueUsd || 0n),
    0n
  );
  const hasCalculationInput = Boolean(
    account &&
      amount > 0n &&
      destination &&
      !isCustomInvalid &&
      !amountExceedsBalance &&
      !amountBelowMinimum
  );
  const quoteReady =
    !shouldSwap || (typeof quote === "object" && "memo" in quote);
  const calculationFailed =
    quote instanceof Error || simulation?.status === "failed";
  const calculationError =
    quote instanceof Error && quote.message
      ? quote.message
      : t("depositCalculationFailed");
  const calculationReady = quoteReady && simulation?.status === "completed";
  const calculationPending =
    hasCalculationInput && !calculationReady && !calculationFailed;
  const sourceOptions: TransferAccountOption[] = accounts.map((item) => ({
    id: transferAccountKey(item),
    address: item.address,
    network: item.asset.chain,
    providers: providersForAddress(connectedAccounts || [], item.address),
    balance: item.balance,
    balanceSymbol: item.asset.metadata.symbol,
    valueUsd: item.valueUsd,
  }));

  return (
    <div className="transfer-modal">
      {onBack && (
        <TransferModalNavigation onBack={onBack}>
          {progress}
        </TransferModalNavigation>
      )}
      <TransferModalHeader
        action={t("deposit")}
        symbol={asset.metadata.symbol}
        suffix={showDestinationSuffix ? t("toRujira") : undefined}
        description={description || t("depositTransactionDescription")}
      />
      <TransferForm
        source={
          <TransferAccountSelector
            label={t("depositFrom")}
            name="deposit-source"
            options={sourceOptions}
            value={account ? transferAccountKey(account) : undefined}
            onChange={(option) => {
              setSourceId(option.id);
              setAmount(0n);
              setSimulation(undefined);
            }}
            onConnect={connect}
            additional={{
              id: "external",
              label: t("otherAddressOrExchange"),
              selected: false,
              disabled: true,
            }}
          />
        }
        amount={
          <TransferAmountField
            label={t("amount")}
            className="bg-black"
            symbol={asset.metadata.symbol}
            amount={amount}
            decimals={8}
            onChangeAmount={(value) => {
              setAmount(value);
              setSimulation(undefined);
            }}
            max={account?.balance || 0n}
            maxLabel={t("max")}
            invalid={amountBelowMinimum || amountExceedsBalance}
            errorTooltip={
              amountExceedsBalance
                ? t("amountExceedsBalance")
                : amountBelowMinimum
                  ? t("minimumDepositAmountError", {
                      amount: dustThreshold
                        ? clipAmountString(dustThreshold, 8)
                        : "",
                      symbol: account?.asset.metadata.symbol || "",
                    })
                  : undefined
            }
            fiat={{ price, symbol: "$" }}
          />
        }
        destination={
          <TransferAccountSelector
            label={t("depositTo")}
            name="deposit-destination"
            options={destinationOptions}
            value={customRecipient ? undefined : destinationId}
            onChange={(option) => {
              setCustomRecipient(false);
              setDestinationId(option.id);
              setSimulation(undefined);
            }}
            onConnect={connect}
            showAddressActions
            custom={{
              selected: customRecipient,
              value: customAddress,
              onSelect: () => {
                setCustomRecipient(true);
                setSimulation(undefined);
              },
              onChange: (value) => {
                setCustomAddress(value);
                setSimulation(undefined);
              },
              placeholder: t("enterNetworkAddress", {
                network: networkLabel(THOR),
              }),
              invalid: isCustomInvalid,
              validationNetwork: networkLabel(THOR),
              validationIsRujira: true,
            }}
          />
        }
        options={
          <>
            <button
              type="button"
              className={clsx(
                "transparent flex w-full jc-c ai-c gap-0.5 mt-1 condensed fs-14 pointer color-grey hover-white",
                { invisible: !advancedOptionsApplicable }
              )}
              aria-expanded={advancedOptionsApplicable ? advanced : undefined}
              aria-hidden={!advancedOptionsApplicable}
              disabled={!advancedOptionsApplicable}
              tabIndex={advancedOptionsApplicable ? undefined : -1}
              onMouseEnter={() => setHoverAdvanced(true)}
              onMouseLeave={() => setHoverAdvanced(false)}
              onClick={() => setAdvanced((current) => !current)}>
              <Animated
                name="filters"
                paused={!hoverAdvanced}
                className={clsx("w-2 h-2", {
                  "color-grey": !hoverAdvanced,
                  "color-white": hoverAdvanced,
                })}
              />
              {advanced ? t("hideAdvancedOptions") : t("showAdvancedOptions")}
            </button>
            {advanced && advancedOptionsApplicable && (
              <div className="mt-1 fs-14">
                <h2 className="fs-14 fw-500 color-grey mt-2.5 mb-1 ml-1">
                  <span
                    className="has-tip no-select"
                    aria-label={depositAsTooltip}
                    data-tooltip-id="modal-tip"
                    data-tooltip-content={depositAsTooltip}
                    style={{ borderBottom: "1px dotted currentColor" }}>
                    {t("depositAssetOnChain")}
                  </span>
                </h2>
                <div className="transfer-modal__accounts br-1 p-2 bg-black mb-2 flex dir-c gap-y-1 condensed">
                  <label
                    className={clsx(
                      "flex ai-c pointer",
                      direct ? "color-grey" : "color-white"
                    )}>
                    <Radio
                      name="deposit-asset-variant"
                      checked={!direct}
                      onChange={() => {
                        setDirect(false);
                        setSimulation(undefined);
                      }}
                    />
                    <NetworkIcon
                      network={asset.chain}
                      className="w-2 h-2 block mx-1"
                    />
                    {canonicalAssetLabel}
                  </label>
                  <label
                    className={clsx(
                      "flex ai-c pointer",
                      direct ? "color-white" : "color-grey"
                    )}>
                    <Radio
                      name="deposit-asset-variant"
                      checked={direct}
                      onChange={() => {
                        setDirect(true);
                        setSimulation(undefined);
                      }}
                    />
                    {account && (
                      <NetworkIcon
                        network={account.asset.chain}
                        className="w-2 h-2 block mx-1"
                      />
                    )}
                    {directAssetLabel}
                  </label>
                </div>
                {direct && (
                  <Warning
                    className="warning--sm condensed flex ai-c mt-1"
                    color="orange">
                    <Animated
                      name="swap"
                      className="color-orange block no-shrink"
                      style={{ width: "2.5rem", height: "2.5rem" }}
                    />
                    <small className="text-left fs-14">
                      <Trans
                        i18nKey="common:nonCanonicalDepositWarning"
                        values={{
                          canonicalAsset: canonicalAssetLabel,
                          selectedAsset: directAssetLabel,
                        }}
                        components={{
                          strong: <strong className="color-white" />,
                        }}
                      />
                    </small>
                  </Warning>
                )}
              </div>
            )}
          </>
        }
        warning={
          customRecipient ? (
            <TransferAddressWarning>
              {t("depositCustomAddressWarning")}
            </TransferAddressWarning>
          ) : undefined
        }
        details={
          <TransactionDetails
            title={t("transactionDetails")}
            titleClassName="mt-1.5 mt-sm-2">
            <TransactionDetailRow
              label={t("minimumDeposit")}
              tooltip={t("minimumDepositTooltip")}>
              <Decimal
                amount={dustThreshold || 0n}
                symbol={account?.asset.metadata.symbol}
                clip
                symbolClassName="color-grey condensed fw-500"
              />
            </TransactionDetailRow>
            <TransactionDetailRow
              label={t("depositTime")}
              tooltip={t("depositTimeTooltip")}>
              <TransferTimeValue network={account?.asset.chain} />
            </TransactionDetailRow>
            <TransactionDetailRow
              label={t("depositFee")}
              tooltip={t("depositFeesTooltip")}>
              <LoaderWithContent
                loading={calculationPending}
                content={
                  calculationReady ? (
                    <TransactionFeeSummary
                      fees={fees}
                      totalValueUsd={totalFeeValueUsd}
                    />
                  ) : calculationFailed ? (
                    <span className="color-red">{calculationError}</span>
                  ) : (
                    <span className="color-grey">&mdash;</span>
                  )
                }
              />
            </TransactionDetailRow>
            <TransactionDetailRow
              label={t("youReceive")}
              tooltip={t("depositReceivedAmountTooltip", {
                chain: account ? networkLabel(account.asset.chain) : "",
              })}>
              <LoaderWithContent
                loading={calculationPending}
                content={
                  calculationReady ? (
                    <>
                      <Decimal
                        amount={received}
                        symbol={receivedAsset.metadata.symbol}
                        clip
                        symbolClassName="color-grey condensed fw-500"
                      />
                      <span className="color-grey ml-1">
                        (
                        <Fiat
                          amount={(receivedPrice * received) / 10n ** 12n}
                          symbol="$"
                          decimals={8}
                          clip
                        />
                        )
                      </span>
                    </>
                  ) : (
                    <span className="color-grey">&mdash;</span>
                  )
                }
              />
            </TransactionDetailRow>
            <TransactionDetailRow label={t("receivingAddress")}>
              <TransactionAddress address={destination} network={THOR} />
            </TransactionDetailRow>
          </TransactionDetails>
        }
        footer={
          <Submit
            selected={account}
            amount={amount}
            target={destination}
            quote={quote}
            shouldSwap={shouldSwap}
            onSuccess={onSuccess}
            cancel={cancel}
            disabled={
              isCustomInvalid ||
              !destination ||
              amountBelowMinimum ||
              amountExceedsBalance
            }
            isDangerous={false}
            onSimulation={setSimulation}
          />
        }
      />
    </div>
  );
};
